import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Bed, Maximize, Calendar, Zap, ArrowLeft, Share2, Heart, Phone, Building2, ChevronLeft, ChevronRight, Send, User, MessageSquare, X, ZoomIn, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import Header from "../components/Header";
import CreditSimulatorAdvanced from "../components/CreditSimulatorAdvanced";
import ChatButton from "../components/ChatButton";
import FavoriteButton from "../components/FavoriteButton";
import LoginPromptModal from "../components/LoginPromptModal";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PropertyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showViewingModal, setShowViewingModal] = useState(false);
  const [showLightbox, setShowLightbox] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptVariant, setLoginPromptVariant] = useState("contact");
  const [viewingForm, setViewingForm] = useState({
    visitor_name: "",
    visitor_phone: "",
    visitor_email: "",
    message: ""
  });
  const [sendingViewing, setSendingViewing] = useState(false);

  const placeholderImages = [
    "https://images.unsplash.com/photo-1663756915304-40b7eda63e41?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzcyODIxODg5fDA&ixlib=rb-4.1.0&q=85",
    "https://images.unsplash.com/photo-1663756915301-2ba688e078cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzcyODIxODg5fDA&ixlib=rb-4.1.0&q=85"
  ];

  useEffect(() => {
    fetchListing();
  }, [id]);

  const fetchListing = async () => {
    try {
      const response = await axios.get(`${API}/listings/${id}`);
      setListing(response.data);
    } catch (error) {
      console.error("Error fetching listing:", error);
      toast.error("Eroare la încărcarea anunțului");
    } finally {
      setLoading(false);
    }
  };

  const handleViewingSubmit = async (e) => {
    e.preventDefault();
    
    if (!viewingForm.visitor_name || !viewingForm.visitor_phone) {
      toast.error("Completează numele și telefonul");
      return;
    }
    
    setSendingViewing(true);
    
    try {
      await axios.post(`${API}/listings/${id}/request-viewing`, {
        listing_id: id,
        ...viewingForm
      });
      
      toast.success("Cererea de vizionare a fost trimisă! Proprietarul va fi notificat.");
      setShowViewingModal(false);
      setViewingForm({ visitor_name: "", visitor_phone: "", visitor_email: "", message: "" });
      
    } catch (error) {
      console.error("Error requesting viewing:", error);
      toast.error("Eroare la trimiterea cererii");
    } finally {
      setSendingViewing(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.share({
        title: listing?.title,
        url: window.location.href
      });
    } catch {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copiat în clipboard");
    }
  };

  const images = listing?.images?.length > 0 ? listing.images : placeholderImages;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const getPropertyTypeLabel = (type) => {
    const types = {
      apartament: "Apartament",
      casa: "Casă",
      teren: "Teren",
      spatiu_comercial: "Spațiu comercial"
    };
    return types[type] || type;
  };

  const getStatusBadge = (status) => {
    const styles = {
      detected: "bg-amber-100 text-amber-800",
      invited: "bg-blue-100 text-blue-800",
      claimed: "bg-emerald-100 text-emerald-800",
      published: "bg-emerald-700 text-white"
    };
    const labels = {
      detected: "Detectat",
      invited: "Invitat",
      claimed: "Revendicat",
      published: "Publicat"
    };
    return (
      <Badge className={styles[status] || "bg-stone-100"}>
        {labels[status] || status}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="animate-pulse">
            <div className="h-96 bg-stone-200 rounded-2xl mb-6" />
            <div className="h-8 bg-stone-200 rounded w-2/3 mb-4" />
            <div className="h-4 bg-stone-200 rounded w-1/3" />
          </div>
        </div>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="min-h-screen bg-stone-100">
        <Header />
        <div className="max-w-7xl mx-auto px-4 py-16 text-center">
          <Building2 className="w-16 h-16 text-stone-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-stone-700 mb-2">Anunț negăsit</h2>
          <p className="text-stone-500 mb-6">Acest anunț nu există sau a fost șters</p>
          <Link to="/">
            <Button className="bg-emerald-950 hover:bg-emerald-900">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Înapoi la anunțuri
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const isUnclaimed = listing.status === "detected" || listing.status === "invited";

  return (
    <div className="min-h-screen bg-stone-100">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 mb-6 text-sm">
          <Link to="/" className="text-stone-500 hover:text-stone-700 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Înapoi
          </Link>
          <span className="text-stone-400">/</span>
          <span className="text-stone-700">{listing.title}</span>
        </div>

        {/* Unclaimed Banner */}
        {isUnclaimed && (
          <div 
            data-testid="unclaimed-banner"
            className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
          >
            <div>
              <p className="font-semibold text-amber-900">Acest anunț a fost identificat online.</p>
              <p className="text-amber-700 text-sm">Dacă ești proprietarul, revendică anunțul pentru a-l publica complet.</p>
            </div>
            <Link to={`/revendica/${listing.id}`}>
              <Button 
                data-testid="claim-listing-button"
                className="bg-amber-500 hover:bg-amber-600 rounded-full px-6"
              >
                Revendică anunțul
              </Button>
            </Link>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Property Info (50%) */}
          <div className="space-y-4 min-w-0">
            {/* Image Gallery - More Compact */}
            <div className="relative rounded-xl overflow-hidden bg-white">
              <div className="aspect-[16/10] relative cursor-pointer group" onClick={() => setShowLightbox(true)}>
                <img
                  data-testid="property-main-image"
                  src={images[currentImageIndex]}
                  alt={listing.title}
                  className="w-full h-full object-cover transition-transform group-hover:scale-105"
                />
                
                {/* Zoom indicator */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 rounded-full p-3">
                    <ZoomIn className="w-6 h-6 text-stone-700" />
                  </div>
                </div>
                
                {images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); prevImage(); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-all"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); nextImage(); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-all"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                      {images.map((_, idx) => (
                        <button
                          key={idx}
                          onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                          className={`w-2 h-2 rounded-full transition-all ${
                            idx === currentImageIndex ? "bg-white w-6" : "bg-white/50"
                          }`}
                        />
                      ))}
                    </div>
                  </>
                )}
                
                <div className="absolute top-4 right-4 flex gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleShare(); }}
                    className="w-10 h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center hover:bg-white transition-all"
                  >
                    <Share2 className="w-4 h-4" />
                  </button>
                  <FavoriteButton listingId={listing.id} size="default" />
                </div>
              </div>
            </div>

            {/* Property Info - Compact */}
            <div className="bg-white rounded-xl p-5 card-shadow">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {getStatusBadge(listing.status)}
                    <Badge variant="outline" className="text-stone-600 text-xs">
                      {getPropertyTypeLabel(listing.property_type)}
                    </Badge>
                  </div>
                  <h1 data-testid="property-title" className="font-['Outfit'] text-xl font-bold text-stone-900">
                    {listing.title}
                  </h1>
                  <div className="flex items-center gap-1 text-stone-500 mt-1 text-sm">
                    <MapPin className="w-3 h-3" />
                    <span>{listing.zone}, {listing.city}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div data-testid="property-price" className="price-badge text-xl">
                    {listing.price.toLocaleString('ro-RO')} €
                  </div>
                  <p className="text-stone-500 text-xs mt-1">
                    {Math.round(listing.price / listing.surface).toLocaleString('ro-RO')} €/mp
                  </p>
                </div>
              </div>

              <Separator className="my-4" />

              {/* Quick Stats - Compact */}
              <div className="grid grid-cols-4 gap-2 mb-4">
                <div className="text-center p-2 bg-stone-50 rounded-lg">
                  <Bed className="w-4 h-4 mx-auto text-stone-600 mb-0.5" />
                  <p className="font-semibold text-sm">{listing.rooms}</p>
                  <p className="text-stone-500 text-xs">Camere</p>
                </div>
                <div className="text-center p-2 bg-stone-50 rounded-lg">
                  <Maximize className="w-4 h-4 mx-auto text-stone-600 mb-0.5" />
                  <p className="font-semibold text-sm">{listing.surface} mp</p>
                  <p className="text-stone-500 text-xs">Suprafață</p>
                </div>
                {listing.floor && (
                  <div className="text-center p-2 bg-stone-50 rounded-lg">
                    <Building2 className="w-4 h-4 mx-auto text-stone-600 mb-0.5" />
                    <p className="font-semibold text-sm">Et. {listing.floor}</p>
                    <p className="text-stone-500 text-xs">Nivel</p>
                  </div>
                )}
                {listing.energy_certificate && (
                  <div className="text-center p-2 bg-green-50 rounded-lg">
                    <Zap className="w-4 h-4 mx-auto text-green-600 mb-0.5" />
                    <p className="font-semibold text-sm">Clasa {listing.energy_certificate}</p>
                    <p className="text-stone-500 text-xs">Energetic</p>
                  </div>
                )}
              </div>
              
              {/* Description - Full */}
              <div>
                <h3 className="font-['Outfit'] font-semibold mb-2">Descriere</h3>
                <p data-testid="property-description" className="text-stone-600 text-sm leading-relaxed whitespace-pre-wrap">
                  {listing.description || "Descriere în curs de completare de către proprietar."}
                </p>
              </div>

              {/* Features - All */}
              {listing.features && listing.features.length > 0 && (
                <div className="mt-4">
                  <h3 className="font-['Outfit'] font-semibold mb-2">Dotări și facilități</h3>
                  <div className="flex flex-wrap gap-2">
                    {listing.features.map((feature, idx) => (
                      <Badge key={idx} variant="secondary" className="px-3 py-1 text-xs">
                        {feature}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Additional Details */}
              <div className="mt-4">
                <h3 className="font-['Outfit'] font-semibold mb-2">Detalii suplimentare</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  {listing.year_built && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                      <span className="text-stone-500">An construcție</span>
                      <span className="font-medium">{listing.year_built}</span>
                    </div>
                  )}
                  {listing.floor !== null && listing.floor !== undefined && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                      <span className="text-stone-500">Etaj</span>
                      <span className="font-medium">{listing.floor}</span>
                    </div>
                  )}
                  {listing.energy_certificate && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                      <span className="text-stone-500">Certificat energetic</span>
                      <span className="font-medium text-green-600">Clasa {listing.energy_certificate}</span>
                    </div>
                  )}
                  {listing.strada && (
                    <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                      <span className="text-stone-500">Stradă</span>
                      <span className="font-medium">{listing.strada}</span>
                    </div>
                  )}
                  <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                    <span className="text-stone-500">Tip tranzacție</span>
                    <span className="font-medium">Vânzare</span>
                  </div>
                  <div className="flex justify-between p-2 bg-stone-50 rounded-lg">
                    <span className="text-stone-500">Preț/mp</span>
                    <span className="font-medium">{Math.round(listing.price / listing.surface).toLocaleString('ro-RO')} €</span>
                  </div>
                </div>
              </div>

              {/* Source */}
              <div className="mt-4 pt-3 border-t border-stone-100">
                <p className="text-stone-400 text-xs">
                  Sursă: {listing.source?.toUpperCase()} • Publicat: {new Date(listing.created_at).toLocaleDateString('ro-RO')}
                  {listing.source_url && (
                    <a href={listing.source_url} target="_blank" rel="noopener noreferrer" className="ml-2 text-amber-600 hover:underline">
                      Vezi anunțul original
                    </a>
                  )}
                </p>
              </div>
            </div>

            {/* Contact Card - Auth-gated */}
            {!isUnclaimed && (
              <div className="bg-white rounded-xl p-3 card-shadow space-y-2">
                {isAuthenticated ? (
                  <>
                    {/* Phone Number - Visible only when logged in */}
                    {(listing.phone || listing.owner_phone) && (
                      <a 
                        href={`tel:${listing.phone || listing.owner_phone}`}
                        data-testid="contact-phone-link"
                        className="flex items-center justify-center gap-2 w-full bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg h-12 font-semibold transition-colors"
                      >
                        <Phone className="w-5 h-5" />
                        <span className="text-lg">{listing.phone || listing.owner_phone}</span>
                      </a>
                    )}
                    
                    <Button
                      data-testid="contact-phone-button"
                      onClick={() => setShowViewingModal(true)}
                      className="w-full bg-emerald-950 hover:bg-emerald-900 rounded-lg h-10 text-sm"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Programeaz&#259; vizionare
                    </Button>
                    
                    {/* Chat Button - only show if listing has owner */}
                    {listing.owner_id && (
                      <ChatButton 
                        listingId={listing.id}
                        listingTitle={listing.title}
                        ownerName={listing.owner_name}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Locked contact - prompt login */}
                    <div 
                      data-testid="contact-locked"
                      className="relative overflow-hidden rounded-lg"
                    >
                      <div className="flex items-center justify-center gap-2 w-full bg-stone-100 text-stone-400 rounded-lg h-12 font-semibold select-none">
                        <Phone className="w-5 h-5" />
                        <span className="text-lg tracking-widest">07** *** ***</span>
                      </div>
                    </div>
                    
                    <Button
                      data-testid="contact-login-prompt-button"
                      onClick={() => {
                        setLoginPromptVariant("contact");
                        setShowLoginPrompt(true);
                      }}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-lg h-11 text-sm font-semibold"
                    >
                      <Lock className="w-4 h-4 mr-2" />
                      Vezi num&#259;rul de telefon
                    </Button>
                    
                    <Button
                      onClick={() => {
                        setLoginPromptVariant("contact");
                        setShowLoginPrompt(true);
                      }}
                      variant="outline"
                      className="w-full rounded-lg h-10 text-sm text-stone-600"
                    >
                      <Calendar className="w-4 h-4 mr-2" />
                      Programeaz&#259; vizionare
                    </Button>
                    
                    <p className="text-center text-stone-400 text-xs pt-1">
                      Autentific&#259;-te gratuit pentru a vedea datele de contact
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Credit Simulator (50%) */}
          <div className="min-w-0">
            <div className="sticky top-20">
              <CreditSimulatorAdvanced 
                propertyPrice={listing.price} 
                listingId={listing.id}
                energyCertificate={listing.energy_certificate || ""}
                propertyType={listing.property_type || "apartament"}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Login Prompt Modal */}
      <LoginPromptModal
        open={showLoginPrompt}
        onOpenChange={setShowLoginPrompt}
        variant={loginPromptVariant}
        returnUrl={`/proprietate/${id}`}
      />

      {/* Viewing Request Modal */}
      <Dialog open={showViewingModal} onOpenChange={setShowViewingModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-emerald-600" />
              Programează o vizionare
            </DialogTitle>
            <DialogDescription>
              Completează datele și proprietarul va fi notificat prin email.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleViewingSubmit} className="space-y-4 mt-4">
            <div>
              <Label htmlFor="visitor_name">Nume complet *</Label>
              <Input
                id="visitor_name"
                placeholder="ex: Ion Popescu"
                value={viewingForm.visitor_name}
                onChange={(e) => setViewingForm({...viewingForm, visitor_name: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="visitor_phone">Telefon *</Label>
              <Input
                id="visitor_phone"
                placeholder="ex: 0721 123 456"
                value={viewingForm.visitor_phone}
                onChange={(e) => setViewingForm({...viewingForm, visitor_phone: e.target.value})}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="visitor_email">Email (opțional)</Label>
              <Input
                id="visitor_email"
                type="email"
                placeholder="ex: ion@email.com"
                value={viewingForm.visitor_email}
                onChange={(e) => setViewingForm({...viewingForm, visitor_email: e.target.value})}
              />
            </div>
            
            <div>
              <Label htmlFor="message">Mesaj (opțional)</Label>
              <Textarea
                id="message"
                placeholder="ex: Sunt disponibil weekend-ul acesta..."
                value={viewingForm.message}
                onChange={(e) => setViewingForm({...viewingForm, message: e.target.value})}
                rows={3}
              />
            </div>
            
            <Button
              type="submit"
              disabled={sendingViewing}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {sendingViewing ? (
                "Se trimite..."
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Trimite cererea
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      {showLightbox && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setShowLightbox(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setShowLightbox(false)}
            className="absolute top-4 right-4 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Image counter */}
          <div className="absolute top-4 left-4 text-white/80 text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>

          {/* Main image */}
          <img
            src={images[currentImageIndex]}
            alt={listing.title}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="w-8 h-8 text-white" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
              >
                <ChevronRight className="w-8 h-8 text-white" />
              </button>
            </>
          )}

          {/* Thumbnail strip */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 p-2 bg-black/50 rounded-xl">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(idx); }}
                  className={`w-16 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                    idx === currentImageIndex ? "border-white" : "border-transparent opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyDetail;
